import { Link, useParams } from 'react-router-dom';
import { ArrowRight, MapPin, Users, TrendingUp, Building2, Check, HelpCircle, ChevronDown, Quote } from 'lucide-react';
import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import ServiceIcon from '../components/ServiceIcon';
import { SERVICES, INDUSTRIES, MARKETS } from '../mock';
import { CITIES } from '../data/cities';
import useSEO from '../hooks/useSEO';

// Inject JSON-LD structured data as a <script> tag in <head>.
// Adds LocalBusiness, FAQPage, and BreadcrumbList schemas for rich SEO signals.
function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
}

export default function CityDetail() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/locations/${slug}` : undefined;
  useSEO({ title: city?.metaTitle, description: city?.metaDesc, canonical });

  const localBusinessSchema = city ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `Ransen \u2014 ${city.name} Marketing Agency`,
    description: city.metaDesc,
    url: canonical,
    areaServed: { '@type': 'City', name: city.name, containedInPlace: city.country },
    address: { '@type': 'PostalAddress', addressLocality: city.name, addressCountry: city.country },
    priceRange: '$$$',
    telephone: '+13435732394',
    email: 'marketing@ransen.ca',
  } : null;

  const faqSchema = city ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: city.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  const breadcrumbSchema = city ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: typeof window !== 'undefined' ? window.location.origin : '' },
      { '@type': 'ListItem', position: 2, name: 'Locations', item: typeof window !== 'undefined' ? `${window.location.origin}/locations` : '' },
      { '@type': 'ListItem', position: 3, name: city.country, item: typeof window !== 'undefined' ? `${window.location.origin}/markets/${city.countrySlug}` : '' },
      { '@type': 'ListItem', position: 4, name: city.name, item: canonical },
    ],
  } : null;

  useJsonLd('local-business-schema', localBusinessSchema);
  useJsonLd('faq-schema', faqSchema);
  useJsonLd('breadcrumb-schema', breadcrumbSchema);

  if (!city) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>City not found</h1>
        <Link to='/locations' className='mt-6 inline-block text-brand'>Back to locations</Link>
      </div>
    );
  }

  const country = MARKETS.find((m) => m.slug === city.countrySlug);
  const otherCities = CITIES.filter((c) => c.countrySlug === city.countrySlug && c.slug !== city.slug);
  const topIndustries = (city.topIndustries || []).map((s) => INDUSTRIES.find((i) => i.slug === s)).filter(Boolean);

  return (
    <div className='bg-void'>
      <PageHeader
        eyebrow={`City Hub · ${city.country}`}
        title={<><span className='text-white'>{city.h1.replace(city.name, '')}</span><span className='text-brand-gradient'>{city.name}</span></>}
        description={city.intro}
        crumbs={[{ label: 'Locations', to: '/locations' }, { label: country?.name || city.country, to: country ? `/markets/${country.slug}` : undefined }, { label: city.name }]}
      />

      {/* Local stats */}
      <section className='py-14'>
        <div className='max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          <StatCard icon={MapPin} value={city.name} label={`${country?.region} hub`} />
          <StatCard icon={Users} value={city.population} label='Metro population' />
          <StatCard icon={Building2} value={`${city.districts.length}+`} label='Business districts covered' />
          <StatCard icon={TrendingUp} value='3.4x' label='Avg. client growth' />
        </div>
      </section>

      {/* Local economy context */}
      <section className='py-10 border-t border-brand/10'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='eyebrow-pill'>Local Market</div>
          <p className='mt-5 text-white/80 text-lg leading-relaxed font-body'>{city.economy}</p>
        </div>
      </section>

      {/* Local pull quote — adds unique on-page content for SEO and social proof */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='glass-card rounded-2xl p-8 md:p-10 relative'>
            <Quote className='w-8 h-8 text-brand mb-4' />
            <p className='font-heading text-xl md:text-2xl text-white leading-relaxed'>
              &ldquo;Working with a marketing partner that actually understands {city.name}’s local market — the districts, the industries, the competitive dynamics — changes what growth looks like. We build programs that feel native, not translated.&rdquo;
            </p>
            <div className='mt-6 pt-6 border-t border-brand/15'>
              <div className='font-heading font-bold text-white text-[15px]'>The Ransen {city.name} Team</div>
              <div className='text-brand text-[12.5px] font-accent tracking-wider uppercase mt-1'>Senior Growth Strategists</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our approach for this city — additional unique content block */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='eyebrow-pill'>Our Approach</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>How Ransen approaches {city.name} marketing</h2>
          <div className='mt-6 space-y-4 text-white/75 text-[15.5px] leading-relaxed font-body'>
            <p>
              We start every {city.name} engagement with a 2-week diagnostic sprint — auditing tracking, media, funnel, and organic surface area. From there, we build a program tuned to {city.name}’s specific market dynamics: {city.districts.slice(0, 3).join(', ')}, and beyond.
            </p>
            <p>
              Our {city.name} clients typically span {city.topIndustries.length}+ industries, from established category leaders to fast-scaling challengers. What connects them: they want senior strategists collaborating in their time zone, native creative that resonates locally, and reporting they can bring straight to a board meeting.
            </p>
            <p>
              As {city.country}’s digital landscape evolves — stricter privacy regulations, rising CPMs, the emergence of AI-driven search and LLM-based discovery — we ship programs designed for what’s coming, not just what worked last year.
            </p>
          </div>
        </div>
      </section>

      {/* Why local businesses choose Ransen */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <h2 className='font-heading text-3xl md:text-4xl font-bold text-white'>Why {city.name} businesses choose Ransen</h2>
          <div className='mt-8 grid sm:grid-cols-2 gap-4'>
            {city.whyChoose.map((point) => (
              <div key={point} className='glass-card rounded-2xl p-5 flex items-start gap-3'>
                <div className='w-8 h-8 rounded-full bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shrink-0'>
                  <Check className='w-4 h-4 text-white' />
                </div>
                <div className='text-white/85 text-[14.5px] leading-relaxed font-body'>{point}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business districts */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Business Districts</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>{city.name} neighborhoods we serve</h2>
          <p className='mt-4 text-white/65 max-w-2xl font-body'>Our {city.name} clients are based across the city’s major business hubs. Local knowledge, city-specific creative, and district-level campaign targeting.</p>
          <div className='mt-8 grid grid-cols-2 md:grid-cols-3 gap-3'>
            {city.districts.map((d) => (
              <div key={d} className='glass-card rounded-xl p-4 flex items-center gap-3'>
                <MapPin className='w-4 h-4 text-brand shrink-0' />
                <div className='text-white/85 text-[14px]'>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top local industries */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Local Industries</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Industries thriving in {city.name}</h2>
          <p className='mt-4 text-white/65 max-w-2xl font-body'>The categories where {city.name} businesses lead — and where we build category-specific playbooks.</p>
          <div className='mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
            {topIndustries.map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} className='glass-card rounded-xl p-4 text-center group'>
                <div className='w-10 h-10 bg-brand/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-brand/30 transition-colors'>
                  <ServiceIcon name={i.icon} className='w-4 h-4 text-brand' />
                </div>
                <div className='font-heading font-bold text-white text-xs leading-tight'>{i.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local marketing challenges */}
      <section className='py-14 border-t border-brand/10 bg-surface'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Local Challenges</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Marketing challenges specific to {city.name}</h2>
          <div className='mt-8 grid sm:grid-cols-2 gap-4'>
            {city.localChallenges.map((c) => (
              <div key={c} className='glass-card rounded-xl p-5 flex items-start gap-3'>
                <div className='w-1.5 h-1.5 rounded-full bg-accent-pink mt-2.5 shrink-0' />
                <div className='text-white/80 text-[14.5px] leading-relaxed font-body'>{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services available */}
      <section className='py-14 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Services</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Full-service growth for {city.name} brands</h2>
          <p className='mt-4 text-white/65 max-w-2xl font-body'>The complete Ransen service stack, available for {city.name}-based brands with local strategy leads and reporting.</p>
          <div className='mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {SERVICES.slice(0, 9).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className='glass-card rounded-2xl p-6 group'>
                <div className='w-12 h-12 bg-gradient-to-br from-brand-dark to-brand rounded-2xl flex items-center justify-center shadow-md shadow-brand/30'>
                  <ServiceIcon name={s.icon} className='w-5 h-5 text-white' />
                </div>
                <h4 className='mt-4 font-heading text-lg font-bold text-white group-hover:text-brand transition-colors'>{s.title}</h4>
                <p className='mt-2 text-white/60 text-[13.5px] leading-relaxed font-body'>{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className='py-14 border-t border-brand/10 bg-surface'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='eyebrow-pill'>Frequently Asked</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>{city.name} FAQ</h2>
          <div className='mt-8 space-y-3'>
            {city.faqs.map((faq, i) => (
              <div key={i} className={`glass-card rounded-2xl transition-all ${openFaq === i ? 'border-brand/40' : ''}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className='w-full flex items-center justify-between text-left p-5'>
                  <div className='flex items-start gap-3'>
                    <HelpCircle className='w-4 h-4 text-brand mt-1 shrink-0' />
                    <div className='font-heading font-bold text-white text-[15px]'>{faq.q}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-brand shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className='px-5 pb-5 pl-12 text-white/70 text-[14.5px] leading-relaxed font-body'>{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other cities in same country */}
      {otherCities.length > 0 && (
        <section className='py-14 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <h3 className='font-heading text-2xl md:text-3xl font-bold text-white'>Other cities we serve in {country?.name}</h3>
            <div className='mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
              {otherCities.map((c) => (
                <Link key={c.slug} to={`/locations/${c.slug}`} className='glass-card rounded-xl p-4 text-center group'>
                  <div className='text-2xl'>{c.flag}</div>
                  <div className='mt-2 font-heading font-bold text-white text-sm'>{c.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className='py-20 border-t border-brand/10 text-center bg-gradient-to-r from-brand-dark to-brand'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Book a strategy call for your {city.name} brand</h3>
          <p className='mt-4 text-white/85 font-body'>Free 30-minute diagnostic with a senior strategist. Local time zone coverage.</p>
          <Link to='/contact' className='mt-8 bg-white text-brand-dark hover:bg-white/90 font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 shadow-lg transition-colors'>
            Book a Strategy Call <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: I, value, label }) {
  return (
    <div className='glass-card rounded-2xl p-5'>
      <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shadow-md shadow-brand/30'>
        <I className='w-4 h-4 text-white' />
      </div>
      <div className='mt-4 font-heading text-xl font-bold text-white'>{value}</div>
      <div className='text-white/55 text-[13px] mt-0.5'>{label}</div>
    </div>
  );
}
